import type { NavMeConfig } from '../types';
import { loadConfig, saveConfig } from '../services/configService';
import { pgUpsert } from '../services/supabaseClient';
import { refreshAccountMaps } from '../services/venuePinService';

/**
 * "Setup Credentials" — MultiSet client ID + secret ONLY (super concern).
 * Map selection moved to the Dashboard's map bar; pin/geocode tools moved
 * there too. The 🔄 Refresh button syncs the account: fetches every map
 * (including new scans) and publishes names + GPS to the world-map registry.
 *
 * Secrets are write-only in the UI and are ALSO stored server-side
 * (platform_credentials, super-only RLS) so the multiset-token broker can
 * serve visitor devices and non-BYO customers.
 */
export class SetupScreen {
  readonly el: HTMLDivElement;

  constructor(onDone: () => void) {
    this.el = document.createElement('div');
    this.el.className = 'screen setup-screen';

    const current = loadConfig();
    const mask = (s: string) => (s ? `${s.slice(0, 3)}•••` : '(not set)');

    this.el.innerHTML = `
      <h1>Setup Credentials</h1>
      <p class="muted">Platform MultiSet account. Fields are write-only — never displayed after saving.</p>
      <p class="muted current-summary">Current: ID ${mask(current.clientId)}</p>
      <label>Client ID
        <input id="su-id" type="text" autocomplete="off" placeholder="MultiSet client ID" />
      </label>
      <label>Client Secret
        <input id="su-secret" type="password" autocomplete="off" placeholder="MultiSet client secret" />
      </label>
      <button id="su-refresh" class="btn ghost">🔄 Refresh — fetch all maps & publish to world map</button>
      <p class="muted hidden" id="su-note"></p>
      <p class="hint">⚠️ This origin must be whitelisted in the MultiSet dashboard:<br/>Credentials → Settings → Domains.</p>
      <button id="su-save" class="btn primary">Save & Continue</button>
    `;

    const $ = <T extends HTMLElement>(sel: string) => this.el.querySelector(sel) as T;
    const note = $('#su-note');

    const creds = () => ({
      clientId: ($('#su-id') as HTMLInputElement).value.trim() || current.clientId,
      clientSecret: ($('#su-secret') as HTMLInputElement).value.trim() || current.clientSecret,
    });

    $('#su-refresh').addEventListener('click', () => {
      const { clientId, clientSecret } = creds();
      note.classList.remove('hidden');
      if (!clientId || !clientSecret) {
        note.textContent = 'Enter Client ID and Secret first (or have them saved).';
        return;
      }
      const btn = $('#su-refresh') as HTMLButtonElement;
      btn.disabled = true;
      refreshAccountMaps(clientId, clientSecret, (msg) => (note.textContent = msg))
        .then((summary) => {
          note.textContent = /permission|policy|row-level|401|403/i.test(summary)
            ? `${summary} — sign in as a Super Admin first, then retry.`
            : summary;
        })
        .catch((err: unknown) => {
          note.textContent = `Refresh failed: ${err instanceof Error ? err.message : String(err)}`;
        })
        .finally(() => {
          btn.disabled = false;
        });
    });

    $('#su-save').addEventListener('click', () => {
      const { clientId, clientSecret } = creds();
      if (!clientId || !clientSecret) {
        alert('Client ID and Client Secret are required.');
        return;
      }
      // Map selection lives in the Dashboard now — keep whatever is active.
      const cfg: NavMeConfig = { ...current, clientId, clientSecret, brokered: false };
      saveConfig(cfg);
      // Server-side copy for the token broker (super-only RLS; non-blocking).
      void pgUpsert(
        'platform_credentials',
        { id: true, client_id: clientId, client_secret: clientSecret },
        'id',
      ).catch((err: unknown) => {
        console.warn('platform credential publish skipped (super sign-in required):', err);
      });
      ($('#su-id') as HTMLInputElement).value = '';
      ($('#su-secret') as HTMLInputElement).value = '';
      onDone();
    });
  }
}
